import axios, {
  AxiosError,
  AxiosHeaders,
  HeadersDefaults,
  RawAxiosRequestHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios'
import type { ApiResponse } from '@/api/types'
import { BizErrorCode as ErrorCode } from '@/api/types'
import { AppConfig } from '@/config'
import { getDeviceInfo } from './device'
import { useUserStore } from '@/stores/user'

export interface RequestOptions {
  showProgress?: boolean
  returnNative?: boolean
  skipErrorHandler?: boolean
}

const baseURL = import.meta.env.VITE_API_BASE_URL || 'https://apiv2.desktopspace.cn'

const service: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  }
})

service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('app-token')
    if (token && config.headers) {
      config.headers.tk = token
    }
    if (config.method === 'post') {
      config.data = { ..._commonParams(), ...config.data }
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const res = response.data
    if (response.config.responseType === 'blob') {
      return response
    }

    if (res.code !== ErrorCode.SUCCESS) {
      handleBusinessError(res.code, res.message)
      return Promise.reject(new Error(res.message || 'Error'))
    }
    return response
  },
  (error: AxiosError<ApiResponse>) => {
    const status = error.response?.status
    const message = error.response?.data?.message

    switch (status) {
      case ErrorCode.UNAUTHORIZED:
        handleBusinessError(ErrorCode.UNAUTHORIZED, message || '登录已过期，请重新登录')
        break
      case ErrorCode.FORBIDDEN:
        handleBusinessError(ErrorCode.FORBIDDEN, message || '没有访问权限')
        break
      case ErrorCode.NOT_FOUND:
        handleBusinessError(ErrorCode.NOT_FOUND, message || '请求的资源不存在')
        break
      default:
        handleBusinessError(ErrorCode.SERVER_ERROR, message || '网络异常，请稍后重试')
    }
    return Promise.reject(error)
  }
)

function handleBusinessError(code: number, message: string): void {
  console.error(`[API Error ${code}] ${message}`)

  if (code === ErrorCode.UNAUTHORIZED && message.includes('Token')) {
    useUserStore().logout()

    // 动态导入 store，避免循环依赖；打开登录弹窗
    // import('@/stores/authModal')
    //   .then(({ useAuthModalStore }) => {
    //     const authModal = useAuthModalStore()
    //     authModal.open({ mode: 'login' })
    //   })
    //   .catch(() => {
    //     // 兜底：store 未就绪时使用 SPA 跳转（不刷新页面）
    //     import('@/router').then(({ default: router }) => {
    //       router.push('/')
    //     }).catch(() => {
    //       window.location.href = '/'
    //     })
    //   })
  }
}

function _commonParams(): RawAxiosRequestHeaders | AxiosHeaders | Partial<HeadersDefaults> {
  const { brand, model, deviceId, os, osVersion } = getDeviceInfo()
  return {
    t: Date.now(),
    appId: AppConfig.appId,
    appVersion: AppConfig.appVersion,
    brand,
    model,
    deviceId,
    os,
    osVersion,
    channel: AppConfig.channel
  }
}

/**
 * 通用请求方法，返回已解包的 data
 * @example const list = await request<User[]>({ url: '/users', method: 'get' })
 */
export function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  return service(config).then((response) => {
    return (response.data as ApiResponse<T>).result
  })
}

export default service
